/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2022, Zumper
 * @description parseFiltersFromSearchObject
 */

import { isQueryTruthy } from '@zcode/nr-utils'
import type { AllowedBathroomCounts, Filters } from '@zcode/state-type'
import { isNumber, isValidJson } from '@zcode/utils'
import { isValid } from 'date-fns'
import type { O } from 'ts-toolbelt'
import {
  ALLOWED_SORT_OPTIONS,
  FILTER_TYPES,
  MAX_BATHROOM_COUNT,
  URL_FILTER_QUERY_KEYS,
} from './filterConstants'

export const parseFiltersFromSearchObject = (search: { [key: string]: any }) => {
  const ageRestricted = search['age-restricted']
  const availableAfter = search['available-after']
  const availableBefore = search['available-before']
  const bathroomsRange = search['bathrooms-range']
  const buildingAmenities = search['building-amenities']
  const encodedFilters = search['encoded-filters']
  const features = search.features
  const feeds = search.feeds
  const floorplans = search.floorplans
  const guests = search.guests
  const hasImages = search.images
  const hasVirtualTours = search['has-virtual-tours']
  const hideNoVacancy = search['hide-no-vacancy']
  const incomeRestricted = search['restricted-income']
  const keywords = search.keywords
  const leaseTerm = search['lease-term']
  const listingAmenities = search['listing-amenities']
  const liveOnlineTours = search['live-online-tours']
  const maxDays = search['max-days']
  const maxPricePerBedroom = search['price-per-bedroom']
  const maxSqft = search['max-square-feet']
  const minBathrooms = Number.parseInt(search.bathrooms, 10)
  const minSqft = search['min-square-feet']
  const neighborhoodIds = search['neighborhood-ids']
  const orAmenities = search['or-amenities']
  const orFeatures = search['or-features']
  const pad = search.pad
  const propertyTypes = search['property-types']
  const Q = search.Q
  const qs = search.qs
  const ratingMin = search['rating-min']
  const ratingMax = search['rating-max']
  const recommended = search.recommended
  const section8 = search.section8
  const sort = search.sort
  const startDate = search[URL_FILTER_QUERY_KEYS[FILTER_TYPES.START_DATE]]
  const endDate = search[URL_FILTER_QUERY_KEYS[FILTER_TYPES.END_DATE]]
  const studentHousing = search['student-housing']
  const transits = search.transits
  const videoTours = search['video-tours']
  const waitlisted = search.waitlisted
  const zappable = search.zappable
  const minActiveUnits = search['min-active-units']

  // NOTE: filters are pojo for the rest of this function
  const filters: O.Writable<Partial<Filters>> = {}

  if (endDate) {
    filters[FILTER_TYPES.END_DATE] = endDate
  }
  if (startDate) {
    filters[FILTER_TYPES.START_DATE] = startDate
  }
  if (minBathrooms) {
    const maxBathroomCount = MAX_BATHROOM_COUNT
    filters.minBathrooms = Math.min(minBathrooms, maxBathroomCount) as AllowedBathroomCounts
  }
  if (bathroomsRange) {
    filters.bathroomsRange = bathroomsRange.split(',').map(Number)
  }
  if (guests) {
    filters.guests = guests.split(',').map(Number)
  }
  if (sort) {
    filters.sort = sort.split(',').filter((option: string) => ALLOWED_SORT_OPTIONS.has(option))
    if (filters.sort && Array.isArray(filters.sort) && filters.sort.length === 0) {
      delete filters.sort
    }
  }
  if (isNumber(maxDays)) {
    filters.maxDays = Number(maxDays)
  }

  if (floorplans) {
    const value = Number.parseInt(floorplans, 10)
    filters.floorplans = isNaN(value) ? undefined : value
  }

  if (transits) {
    const parsedTransits: { [key: string]: string } = {}
    try {
      decodeURIComponent(transits)
        .split(',')
        .forEach((t) => {
          const transitDistance = t.split(/=/)
          parsedTransits[transitDistance[0]] = transitDistance[1]
        })
      filters.transits = parsedTransits
    } catch {
      filters.transits = null
    }
  }

  if (leaseTerm) {
    filters.shortTerm = leaseTerm.includes('short') || null
    filters.longTerm = false
  }

  if (minSqft && isNumber(minSqft)) {
    filters.minSquareFeet = Number(minSqft)
  }

  if (maxSqft && isNumber(maxSqft)) {
    filters.maxSquareFeet = Number(maxSqft)
  }

  if (availableAfter && (isValid(new Date(availableAfter)) || isNumber(availableAfter))) {
    filters.availableAfter = availableAfter
  }

  if (availableBefore && (isValid(new Date(availableBefore)) || isNumber(availableBefore))) {
    filters.availableBefore = availableBefore
  }

  if (feeds) {
    filters.feeds = feeds
  }

  if (neighborhoodIds) {
    const neighborhoodIdsArray = neighborhoodIds
      .split(',')
      .map((id: string) => Number.parseInt(id, 10))
    if (neighborhoodIdsArray.length) {
      filters.neighborhoodIds = neighborhoodIdsArray
    }
  }

  if (orAmenities && isValidJson(orAmenities)) {
    filters.orAmenities = JSON.parse(orAmenities)
  }

  if (orFeatures && isValidJson(orFeatures)) {
    filters.orFeatures = JSON.parse(orFeatures)
  }

  if (isQueryTruthy(pad) && pad !== '0') {
    filters.pad = true
  }

  if (isQueryTruthy(zappable) && zappable !== '0') {
    filters.isZappable = true
  }

  if (isQueryTruthy(hasImages)) {
    filters.hasImages = true
  }

  if (isQueryTruthy(hasVirtualTours)) {
    filters.hasVirtualTours = true
  }

  if (isQueryTruthy(incomeRestricted)) {
    filters.incomeRestricted = true
  }

  if (isQueryTruthy(liveOnlineTours)) {
    filters.liveOnlineTours = true
  }

  if (isQueryTruthy(section8)) {
    filters.section8 = true
  }

  if (isQueryTruthy(ageRestricted)) {
    filters.ageRestricted = true
  }

  if (isQueryTruthy(studentHousing)) {
    filters.studentHousing = true
  }

  if (isQueryTruthy(encodedFilters)) {
    filters.encodedFilters = encodedFilters
  }

  if (isQueryTruthy(listingAmenities)) {
    filters.listingAmenities = listingAmenities.split(',').map(Number)
  }

  if (isQueryTruthy(buildingAmenities)) {
    filters.buildingAmenities = buildingAmenities.split(',').map(Number)
  }

  if (isQueryTruthy(propertyTypes)) {
    filters.propertyTypes = propertyTypes.split(',').map(Number)
  }

  if (isQueryTruthy(features)) {
    filters.features = features.split(',').map(Number)
  }

  if (isQueryTruthy(listingAmenities)) {
    filters.listingAmenities = listingAmenities.split(',').map(Number)
  }

  if (isQueryTruthy(buildingAmenities)) {
    filters.buildingAmenities = buildingAmenities.split(',').map(Number)
  }

  if (isQueryTruthy(features)) {
    filters.features = features.split(',').map(Number)
  }

  if (isQueryTruthy(qs)) {
    filters.qs = qs
  }

  if (isQueryTruthy(Q)) {
    filters.Q = Q
  }

  if (isQueryTruthy(videoTours)) {
    filters.videoTours = true
  }

  if (isQueryTruthy(recommended)) {
    filters.recommended = true
  }

  if (isQueryTruthy(hideNoVacancy)) {
    filters.hideNoVacancy = true
  }

  filters.maxPricePerBedroom = maxPricePerBedroom || null
  filters.keywords = keywords || null
  if (waitlisted === 'false') {
    filters.waitlisted = false
  }

  filters.rating = { min: null, max: null }

  if (ratingMin) {
    filters.rating.min = Number(ratingMin)
  }
  if (ratingMax) {
    filters.rating.max = Number(ratingMax)
  }

  filters.minActiveUnits = Number(minActiveUnits) || null

  return filters
}
